/**
 * ひらがな / カタカナ → ローマ字（ヘボン式ベース）変換。
 * アカウント登録の姓名入力で「みょうじ・なまえ」から familyNameHepburn / firstNameHepburn を作るために使う。
 *
 * 方針（パスポート（ヘボン式）での氏名表記に合わせる。名刺の英字表記で自然な形にする）:
 *  - 2文字の拗音（きゃ→kya 等）を先に判定し、次に1文字を変換する
 *  - っ（促音）は次の子音を重ねる（さっぽろ→sapporo）。語末の っ は無視する
 *  - ん は n。母音・や行が続くときは n'（しんいち→shin'ichi）、ば/ま/ぱ行が続くときは m（なんば→namba）
 *  - 長音は伸ばさない（たろう→Taro、こんどう→Kondo、ゆうき→Yuki、おおの→Ono）。ただし いい は ii のまま
 *  - 長音符（ー）は落とす
 *  - 変換できない文字（漢字・英数字など）はそのまま残す
 */

/** カタカナをひらがなへ寄せる（変換表をひらがなだけで持つため） */
function katakanaToHiragana(value: string): string {
  return value.replace(/[ァ-ヶ]/g, (char) => String.fromCharCode(char.charCodeAt(0) - 0x60))
}

/** 拗音など2文字で1音になるもの */
// prettier-ignore
const DIGRAPHS: Record<string, string> = {
  きゃ: 'kya', きゅ: 'kyu', きょ: 'kyo',
  ぎゃ: 'gya', ぎゅ: 'gyu', ぎょ: 'gyo',
  しゃ: 'sha', しゅ: 'shu', しょ: 'sho', しぇ: 'she',
  じゃ: 'ja', じゅ: 'ju', じょ: 'jo', じぇ: 'je',
  ちゃ: 'cha', ちゅ: 'chu', ちょ: 'cho', ちぇ: 'che',
  にゃ: 'nya', にゅ: 'nyu', にょ: 'nyo',
  ひゃ: 'hya', ひゅ: 'hyu', ひょ: 'hyo',
  びゃ: 'bya', びゅ: 'byu', びょ: 'byo',
  ぴゃ: 'pya', ぴゅ: 'pyu', ぴょ: 'pyo',
  みゃ: 'mya', みゅ: 'myu', みょ: 'myo',
  りゃ: 'rya', りゅ: 'ryu', りょ: 'ryo',
  ふぁ: 'fa', ふぃ: 'fi', ふぇ: 'fe', ふぉ: 'fo',
  うぁ: 'wa', うぃ: 'wi', うぇ: 'we', うぉ: 'wo',
  てぃ: 'ti', でぃ: 'di', とぅ: 'tu', どぅ: 'du',
  ゔぁ: 'va', ゔぃ: 'vi', ゔぇ: 've', ゔぉ: 'vo',
}

/** 1文字の音 */
// prettier-ignore
const MONOGRAPHS: Record<string, string> = {
  あ: 'a', い: 'i', う: 'u', え: 'e', お: 'o',
  か: 'ka', き: 'ki', く: 'ku', け: 'ke', こ: 'ko',
  が: 'ga', ぎ: 'gi', ぐ: 'gu', げ: 'ge', ご: 'go',
  さ: 'sa', し: 'shi', す: 'su', せ: 'se', そ: 'so',
  ざ: 'za', じ: 'ji', ず: 'zu', ぜ: 'ze', ぞ: 'zo',
  た: 'ta', ち: 'chi', つ: 'tsu', て: 'te', と: 'to',
  だ: 'da', ぢ: 'ji', づ: 'zu', で: 'de', ど: 'do',
  な: 'na', に: 'ni', ぬ: 'nu', ね: 'ne', の: 'no',
  は: 'ha', ひ: 'hi', ふ: 'fu', へ: 'he', ほ: 'ho',
  ば: 'ba', び: 'bi', ぶ: 'bu', べ: 'be', ぼ: 'bo',
  ぱ: 'pa', ぴ: 'pi', ぷ: 'pu', ぺ: 'pe', ぽ: 'po',
  ま: 'ma', み: 'mi', む: 'mu', め: 'me', も: 'mo',
  や: 'ya', ゆ: 'yu', よ: 'yo',
  ら: 'ra', り: 'ri', る: 'ru', れ: 're', ろ: 'ro',
  わ: 'wa', ゐ: 'i', ゑ: 'e', を: 'o',
  ぁ: 'a', ぃ: 'i', ぅ: 'u', ぇ: 'e', ぉ: 'o',
  ゃ: 'ya', ゅ: 'yu', ょ: 'yo', ゎ: 'wa',
  ゔ: 'vu',
}

const SMALL_TSU = 'っ'
const N = 'ん'
const LONG_MARK = 'ー'

/**
 * ひらがな（カタカナ可）をローマ字にする。先頭だけ大文字にして名前として使える形で返す。
 * 変換対象が無ければ空文字を返す。
 */
export function kanaToRomaji(value: string | null | undefined): string {
  const source = katakanaToHiragana((value ?? '').trim())
  if (!source) return ''

  let result = ''
  let index = 0

  while (index < source.length) {
    const pair = source.slice(index, index + 2)

    if (DIGRAPHS[pair]) {
      result += DIGRAPHS[pair]
      index += 2
      continue
    }

    const char = source[index]

    if (char === LONG_MARK) {
      index += 1
      continue
    }

    if (char === SMALL_TSU) {
      // 次の音の頭の子音を重ねる（次が無い / 母音始まりなら重ねない）
      const nextPair = source.slice(index + 1, index + 3)
      const nextRomaji = DIGRAPHS[nextPair] ?? MONOGRAPHS[source[index + 1] ?? ''] ?? ''
      const head = nextRomaji.charAt(0)
      if (head && !'aiueo'.includes(head)) {
        // ch は tch ではなく cch とせず、慣用の tch に寄せる（まっちゃ→matcha）
        result += head === 'c' ? 't' : head
      }
      index += 1
      continue
    }

    if (char === N) {
      const nextPair = source.slice(index + 1, index + 3)
      const nextRomaji = DIGRAPHS[nextPair] ?? MONOGRAPHS[source[index + 1] ?? ''] ?? ''
      const head = nextRomaji.charAt(0)
      if (head && ('aiueo'.includes(head) || head === 'y')) {
        result += "n'"
      } else if (head && 'bmp'.includes(head)) {
        result += 'm'
      } else {
        result += 'n'
      }
      index += 1
      continue
    }

    // 長音（おう / おお / うう / ええ）は伸ばさない。直前の母音と同じ伸ばし音なら落とす。
    // ただし次が母音のときは長音ではなく別の音なので残す（いのうえ→Inoue）。
    const tail = result.slice(-1)
    const nextChar = source[index + 1] ?? ''
    const nextIsVowel = nextChar !== '' && 'あいうえお'.includes(nextChar)
    const isLongVowel =
      (char === 'う' && (tail === 'o' || tail === 'u')) ||
      (char === 'お' && tail === 'o') ||
      (char === 'え' && tail === 'e')
    if (isLongVowel && !nextIsVowel) {
      index += 1
      continue
    }

    result += MONOGRAPHS[char] ?? char
    index += 1
  }

  return result.charAt(0).toUpperCase() + result.slice(1)
}
